import { EtfEyebrow } from "@/components/layout/EtfEyebrow";
import { EtfNewsletter } from "@/components/layout/EtfNewsletter";
import { Seo } from "@/lib/seo";
import { COMPANY_HERO, COMPANY_LETTER, COMPANY_PARENT } from "../data/company";

// CompanyPage — "About Corgi Funds".
// Ported 1:1 from dane main (etf-website/company/page.tsx). Three sections:
//   1. Centered hero with the background image rendered through a radial
//      gradient mask (via the `before:` pseudo-element) so the headline
//      stays legible.
//   2. Letter / mission — the 4-paragraph signoff letter.
//   3. Parent company — eyebrow + accented title + 2-column cells + Visit CTA.
// SEO head/JSON-LD hooks from source are dropped; the hero S3 URL is swapped
// for the local `/images/etf/about-us-hero-bg.webp` asset.

export function CompanyPage() {
  return (
    <>
      <Seo
        title="About Corgi Funds"
        description="Corgi Funds is a new breed of ETF issuer, building thematic, leveraged, buffer, and fixed-income strategies for a broader audience of investors."
        canonicalPath="/company"
      />
      {/* ─── Hero ─── */}
      <section className="animate-[etfFadeUp_0.5s_ease_both] relative flex items-center justify-center text-center min-h-[420px] px-10 py-[100px] overflow-hidden max-md:px-6 max-md:py-16 max-md:min-h-[320px] max-[480px]:px-5 max-[480px]:py-[60px] max-[480px]:min-h-[280px] before:content-[''] before:absolute before:inset-0 before:bg-[url('/images/etf/about-us-hero-bg.webp')] before:bg-cover before:bg-[center_bottom] before:z-0">
        <h1 className="relative z-[1] font-[family-name:var(--font-f37bolton)] text-[clamp(48px,6vw,80px)] font-normal leading-[1.05] tracking-[-0.02em] text-[var(--etf-black)] [[data-theme='light']_&]:text-[#1a1510] max-w-[1000px] px-[100px] py-[60px] bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.85)_0%,rgba(255,255,255,0.4)_40%,rgba(255,255,255,0)_75%)] max-md:text-[clamp(36px,9vw,64px)] max-md:px-6 max-md:py-8 max-[480px]:text-[clamp(28px,10vw,44px)] max-[480px]:px-4 max-[480px]:py-5">
          {COMPANY_HERO.title}
        </h1>
      </section>

      {/* ─── Letter / Mission ─── */}
      <section className="animate-[etfFadeUp_0.5s_ease_0.1s_both] bg-[var(--etf-black2)] border-b border-[var(--etf-border)] px-10 pt-20 pb-[100px] flex justify-center max-md:px-5 max-md:pt-14 max-md:pb-16">
        <div className="max-w-[580px] w-full">
          <div className="text-[15px] font-light text-[var(--etf-white2)] leading-[1.8] [&>p+p]:mt-5">
            {COMPANY_LETTER.paragraphs.map((p) => (
              <p key={p.text}>{p.text}</p>
            ))}
          </div>
          <div className="mt-8 text-[15px] font-light text-[var(--etf-white2)] leading-[1.8] [&>strong]:text-[var(--etf-white)] [&>strong]:font-medium">
            {COMPANY_LETTER.signOff}
            <br />
            <strong>{COMPANY_LETTER.signName}</strong>
          </div>
        </div>
      </section>

      {/* ─── Parent Company ─── */}
      <section className="animate-[etfFadeUp_0.5s_ease_0.15s_both] border-b border-[var(--etf-border)] px-10 py-20 max-w-[1200px] mx-auto max-md:px-5 max-md:py-12">
        <div className="mb-10">
          <EtfEyebrow>{COMPANY_PARENT.eyebrow}</EtfEyebrow>
          <h2 className="font-[family-name:var(--font-f37bolton)] text-[clamp(32px,4vw,52px)] leading-none tracking-[-0.02em] text-[var(--etf-white)] mb-5 [&>em]:not-italic [&>em]:text-[var(--etf-orange)]">
            {COMPANY_PARENT.title}
            <em>{COMPANY_PARENT.titleAccent}</em> Company
          </h2>
          <p className="text-base italic font-light text-[var(--etf-white2)] leading-[1.75] mb-7 max-w-[480px]">
            {COMPANY_PARENT.description}
          </p>
          <a
            href={COMPANY_PARENT.ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            className="font-[family-name:var(--font-instrument)] text-sm text-[var(--etf-orange)] no-underline tracking-[0.06em] inline-flex items-center gap-1.5 transition-[gap] duration-200 hover:gap-2.5"
          >
            {COMPANY_PARENT.ctaLabel} &rarr;
          </a>
        </div>

        <div className="grid grid-cols-2 gap-px bg-[var(--etf-border)] border border-[var(--etf-border)] max-lg:grid-cols-1">
          {COMPANY_PARENT.columns.map((col) => (
            <div key={col.label} className="bg-[var(--etf-black)] p-[36px_32px]">
              <div className="font-[family-name:var(--font-instrument)] text-xs tracking-[0.14em] uppercase text-[var(--etf-orange)] mb-4 flex items-center gap-2.5 after:content-[''] after:flex-1 after:h-px after:bg-[var(--etf-border)]">
                {col.label}
              </div>
              <div className="font-[family-name:var(--font-f37bolton)] text-[22px] tracking-[-0.02em] text-[var(--etf-white)] mb-3 leading-none">
                {col.title}
              </div>
              <div className="text-sm italic font-light text-[var(--etf-white2)] leading-[1.7]">
                {col.description}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Newsletter ─── */}
      <EtfNewsletter />
    </>
  );
}
